import { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { UserPlus, Trash, Users, Eye, EyeSlash, ArrowsClockwise, Database } from '@phosphor-icons/react';
import { createUser, listUsers, deleteUser, updateUserTiers, startPriceSync, getPriceSyncStatus, getCachedPriceStats, startDeepSync, getDeepSyncStatus } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const TIER_OPTIONS = [
  { key: 'vendor', label: 'Vendor (Cost)', color: 'bg-slate-100 text-slate-600 border-slate-300' },
  { key: 'retail', label: 'Retail', color: 'bg-red-50 text-red-600 border-red-300' },
  { key: 'website', label: 'Website', color: 'bg-sky-50 text-sky-700 border-sky-300' },
  { key: 'reseller', label: 'Reseller', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
];

function TierToggle({ tiers, onChange, disabled }) {
  return (
    <div className="flex gap-1.5 flex-wrap">
      {TIER_OPTIONS.map(t => {
        const active = tiers.includes(t.key);
        return (
          <button key={t.key} type="button" disabled={disabled}
            data-testid={`tier-toggle-${t.key}`}
            onClick={() => {
              const newTiers = active ? tiers.filter(x => x !== t.key) : [...tiers, t.key];
              if (newTiers.length > 0) onChange(newTiers);
            }}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
              active ? t.color : 'bg-white text-slate-400 border-slate-200 opacity-50'
            }`}
          >
            {active ? <Eye size={12} weight="bold" /> : <EyeSlash size={12} />}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function PriceSyncPanel() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [deepStatus, setDeepStatus] = useState(null);
  const [deepPolling, setDeepPolling] = useState(false);

  const loadStats = async () => {
    try {
      const s = await getCachedPriceStats();
      setStats(s);
    } catch { /* silent */ }
  };

  useEffect(() => { loadStats(); }, []);

  useEffect(() => {
    if (!polling) return;
    const iv = setInterval(async () => {
      try {
        const s = await getPriceSyncStatus();
        setStatus(s);
        if (!s.running) {
          setPolling(false);
          loadStats();
          if (s.ok > 0) toast.success(`Re-sync complete: ${s.ok} products updated, ${s.failed} failed`);
        }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [polling]);

  useEffect(() => {
    if (!deepPolling) return;
    const iv = setInterval(async () => {
      try {
        const s = await getDeepSyncStatus();
        setDeepStatus(s);
        if (!s.running) {
          setDeepPolling(false);
          loadStats();
          if (s.ok > 0) toast.success(`Deep sync complete: ${s.ok} entries cached, ${s.failed} failed`);
        }
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [deepPolling]);

  const handleStart = async () => {
    const ok = window.confirm(
      "Re-fetch DEFAULT vendor prices (one per size) for all products?\n\n" +
      "This is a quick refresh covering the most common configs.\n" +
      "Takes 8–12 minutes. Your manual pricing overrides will NOT be affected."
    );
    if (!ok) return;
    try {
      await startPriceSync();
      toast.success('Quick re-sync started');
      setPolling(true);
      const s = await getPriceSyncStatus();
      setStatus(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start sync');
    }
  };

  const handleDeepSync = async () => {
    const ok = window.confirm(
      "DEEP SYNC — pull every Size × Turnaround combination for every product?\n\n" +
      "This rebuilds the pricing database so employees almost never trigger a vendor call.\n" +
      "• Takes 30–60 minutes\n" +
      "• Runs in the background — you can leave this page\n" +
      "• Your manual pricing overrides will NOT be affected\n" +
      "• Should be run only when A-Plus changes prices (rare)\n\n" +
      "Continue?"
    );
    if (!ok) return;
    try {
      await startDeepSync();
      toast.success('Deep sync started — this will run for 30–60 min');
      setDeepPolling(true);
      const s = await getDeepSyncStatus();
      setDeepStatus(s);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start deep sync');
    }
  };

  const lastSynced = stats?.latest_cached_at
    ? new Date(stats.latest_cached_at).toLocaleString()
    : 'Never';

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 shadow-sm mb-8">
      <h2 className="text-lg font-semibold text-[#0F172A] mb-1 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
        <Database size={20} className="text-[#2563EB]" /> Vendor Price Re-sync
      </h2>
      <p className="text-sm text-[#64748B] mb-4">
        Refresh the local pricing database from the vendor. Run this only when vendor prices change —
        the app serves all pricing from the local database for speed and reliability.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
          <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Cached entries</div>
          <div className="text-2xl font-bold text-[#0F172A]">{stats?.total_entries ?? '–'}</div>
        </div>
        <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
          <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Products covered</div>
          <div className="text-2xl font-bold text-[#0F172A]">{stats?.products_covered ?? '–'}</div>
        </div>
        <div className="p-3 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0]">
          <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Last synced</div>
          <div className="text-sm font-semibold text-[#0F172A] mt-1">{lastSynced}</div>
        </div>
      </div>
      {status?.running && (
        <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
          <div className="text-sm font-medium text-blue-900">
            Syncing… {status.current}/{status.total} — {status.last_product}
          </div>
          <div className="mt-2 h-2 rounded-full bg-blue-100 overflow-hidden">
            <div className="h-full bg-blue-600 transition-all"
              style={{ width: `${status.total ? (status.current / status.total) * 100 : 0}%` }} />
          </div>
        </div>
      )}
      {deepStatus?.running && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <div className="text-sm font-medium text-emerald-900">
            Deep syncing… {deepStatus.current}/{deepStatus.total} — {deepStatus.current_product} ({deepStatus.ok} entries cached)
          </div>
          <div className="mt-2 h-2 rounded-full bg-emerald-100 overflow-hidden">
            <div className="h-full bg-emerald-600 transition-all"
              style={{ width: `${deepStatus.total ? (deepStatus.current / deepStatus.total) * 100 : 0}%` }} />
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <Button data-testid="price-sync-btn" onClick={handleStart} disabled={status?.running || deepStatus?.running}
          className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-10 px-5 font-semibold flex items-center gap-2">
          <ArrowsClockwise size={18} weight="bold" className={status?.running ? 'animate-spin' : ''} />
          {status?.running ? 'Syncing…' : 'Quick Re-sync (defaults)'}
        </Button>
        <Button data-testid="deep-sync-btn" onClick={handleDeepSync} disabled={deepStatus?.running || status?.running}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-10 px-5 font-semibold flex items-center gap-2">
          <ArrowsClockwise size={18} weight="bold" className={deepStatus?.running ? 'animate-spin' : ''} />
          {deepStatus?.running ? 'Deep syncing…' : 'Deep Sync (Size × Turnaround)'}
        </Button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newTiers, setNewTiers] = useState(['retail']);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { setUsers(await listUsers()); } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) { toast.error('All fields required'); return; }
    setCreating(true);
    try {
      await createUser(email, password, name, newTiers);
      toast.success(`User ${name} created`);
      setName(''); setEmail(''); setPassword(''); setNewTiers(['retail']);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally { setCreating(false); }
  };

  const handleDelete = async (userEmail) => {
    if (!window.confirm(`Delete ${userEmail}?`)) return;
    try {
      await deleteUser(userEmail);
      toast.success('User deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleTierChange = async (userEmail, newTierAccess) => {
    try {
      await updateUserTiers(userEmail, newTierAccess);
      toast.success('Tier access updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update tiers');
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
      <Sidebar />
      <main className="flex-1 p-6 md:p-8 overflow-auto">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold tracking-tight text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit' }}>Manage Users</h1>
          <p className="text-[#475569] mb-8">Create employee accounts and control which pricing tiers they can see</p>

          <PriceSyncPanel />

          {/* Create User Form */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 shadow-sm mb-8">
            <h2 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <UserPlus size={20} className="text-[#2563EB]" /> Create Employee Account
            </h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A]">Name</Label>
                  <Input data-testid="new-user-name" value={name} onChange={e => setName(e.target.value)} placeholder="John Doe"
                    className="h-10 rounded-lg border-[#E2E8F0] mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A]">Email</Label>
                  <Input data-testid="new-user-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="john@dplus.ai"
                    className="h-10 rounded-lg border-[#E2E8F0] mt-1" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A]">Password</Label>
                  <Input data-testid="new-user-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Secure password"
                    className="h-10 rounded-lg border-[#E2E8F0] mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A] mb-2 block">Visible Pricing Tiers</Label>
                <TierToggle tiers={newTiers} onChange={setNewTiers} />
                <p className="text-xs text-[#94A3B8] mt-1">Select which pricing tiers this employee can see</p>
              </div>
              <Button data-testid="create-user-btn" type="submit" disabled={creating}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-lg h-10 px-6 font-semibold">
                {creating ? 'Creating...' : 'Create User'}
              </Button>
            </form>
          </div>

          {/* User List */}
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center gap-2">
              <Users size={18} className="text-[#475569]" />
              <h2 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit' }}>All Users</h2>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F8FAFC]">
                  <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8]">Name</TableHead>
                  <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8]">Email</TableHead>
                  <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8]">Role</TableHead>
                  <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8]">Tier Access</TableHead>
                  <TableHead className="text-xs font-semibold tracking-wider uppercase text-[#94A3B8] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u, i) => (
                  <TableRow key={i} className="hover:bg-[#F8FAFC]">
                    <TableCell className="text-sm font-medium text-[#0F172A]">{u.name}</TableCell>
                    <TableCell className="text-sm text-[#475569]">{u.email}</TableCell>
                    <TableCell>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${u.role === 'admin' ? 'bg-[#2563EB]/10 text-[#2563EB]' : 'bg-[#F1F5F9] text-[#475569]'}`}>
                        {u.role}
                      </span>
                    </TableCell>
                    <TableCell>
                      <TierToggle
                        tiers={u.tier_access || ['vendor', 'retail', 'premium']}
                        onChange={(tiers) => handleTierChange(u.email, tiers)}
                        disabled={u.role === 'admin'}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {u.role !== 'admin' && (
                        <button data-testid={`delete-user-${u.email}`} onClick={() => handleDelete(u.email)}
                          className="text-[#94A3B8] hover:text-red-500 transition-colors p-1">
                          <Trash size={16} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
}
